import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { AdminSubNav, SUBNAV_SCHEDULE } from '../../components/admin/AdminSubNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PT = 'America/Los_Angeles';
const time = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-US', { timeZone: PT, hour: 'numeric', minute: '2-digit' }) : '');
const stamp = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: PT, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const todayPT = () => new Date().toLocaleDateString('en-CA', { timeZone: PT });

const useHeaders = () => { const { token } = useAuth(); return useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]); };

export function AdminScheduleDayPage() {
  const headers = useHeaders();
  const [date, setDate] = useState(todayPT());
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); axios.get(`${API_URL}/api/admin/schedule/day`, { headers, params: { date } }).then((r) => setD(r.data)).catch(() => setD(false)); }, [headers, date]);
  const unassigned = d ? d.visits.filter((v) => v.unassigned).length : 0;
  return (
    <div data-testid="schedule-day-page">
      <AdminSubNav section="Schedule" items={SUBNAV_SCHEDULE} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="h-10 rounded-md border border-[#dcded2] bg-white px-3 text-sm" data-testid="schedule-day-date" />
          {d && <span className="text-sm text-[#6f7264]" data-testid="schedule-day-counts"><b className="text-[#20211d]">{d.visits.length}</b> visits · <b className={unassigned ? 'text-[#a4471b]' : 'text-[#20211d]'}>{unassigned}</b> unassigned</span>}
        </div>
        {d && d.blocks?.length > 0 && (
          <ul className="space-y-1.5" data-testid="schedule-day-blocks">
            {d.blocks.map((b) => (
              <li key={b.id} className="flex flex-wrap gap-x-4 rounded-[10px] border border-dashed border-[#b9b7aa] bg-[#efeee6] px-5 py-3 text-sm" data-testid={`schedule-day-block-${b.id}`}>
                <span className="w-28 font-semibold text-[#3f4038]">{time(b.start_time)}–{time(b.end_time)}</span>
                <span className="flex-1 text-[#3f4038]">⊘ {b.label}</span>
                <span className="text-xs text-[#5f6455]">{b.cleaner_name || 'Whole business'} · Unavailable</span>
              </li>
            ))}
          </ul>
        )}
        {d === null ? <Loader2 className="h-5 w-5 animate-spin text-[#f36a19]" /> : d === false ? <p className="text-sm text-[#a4471b]">The day could not be loaded.</p> : d.visits.length === 0 ? <p className="text-sm text-[#77776e]" data-testid="schedule-day-empty">No visits on this day.</p> : (
          <ul className="rounded-[10px] border border-[#e3e0d6] bg-[#fbfaf7] divide-y divide-[#ecebe4]">
            {d.visits.map((v) => (
              <li key={v.id} className="flex flex-wrap items-center gap-x-5 gap-y-1 px-5 py-4" data-testid={`schedule-day-visit-${v.id}`}>
                <span className="w-28 text-sm font-semibold text-[#20211d]">{time(v.start_time)}{v.end_time ? `–${time(v.end_time)}` : ''}</span>
                <span className="flex-1 min-w-[200px] text-sm text-[#20211d]">{v.client} · {v.service}{v.property ? <span className="block text-xs text-[#77776e]">{v.property}</span> : null}</span>
                <span className={`text-xs ${v.unassigned ? 'text-[#a4471b] font-semibold' : 'text-[#5f6455]'}`}>{v.unassigned ? 'No Guru assigned' : v.gurus.join(', ')}</span>
                <span className="text-[10px] px-2 py-0.5 rounded bg-[#efeee6] text-[#5f6455]">{v.status}</span>
                <Link to={`/admin/appointments/${v.id}`} className="text-xs font-semibold text-[#b75b28]">Open ↗</Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

export function AdminScheduleChangesPage() {
  const headers = useHeaders();
  const [rows, setRows] = useState(null);
  useEffect(() => { axios.get(`${API_URL}/api/admin/schedule/changes`, { headers }).then((r) => setRows(r.data)).catch(() => setRows(false)); }, [headers]);
  return (
    <div data-testid="schedule-changes-page">
      <AdminSubNav section="Schedule" items={SUBNAV_SCHEDULE} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {rows === null ? <Loader2 className="h-5 w-5 animate-spin text-[#f36a19]" /> : rows === false ? <p className="text-sm text-[#a4471b]">Change history could not be loaded.</p> : rows.length === 0 ? <p className="text-sm text-[#77776e]">No visit changes recorded yet.</p> : (
          <ul className="rounded-[10px] border border-[#e3e0d6] bg-[#fbfaf7] divide-y divide-[#ecebe4]" data-testid="schedule-changes-list">
            {rows.map((r, i) => (
              <li key={r.id || i} className="flex flex-wrap gap-x-5 gap-y-1 px-5 py-3.5 text-sm" data-testid={`schedule-change-${r.id || i}`}>
                <span className="w-32 text-xs text-[#77776e]">{stamp(r.at)} PT</span>
                <span className="flex-1 min-w-[220px] text-[#20211d]"><b>{r.status}</b>{r.note ? ` · ${r.note}` : ''}<span className="block text-xs text-[#77776e]">{r.client || 'Visit'}{r.visit_start ? ` · visit ${stamp(r.visit_start)}` : ''} · by {r.actor}</span></span>
                {r.kind === 'block' ? <span className="text-xs text-[#5f6455]">Blocked time</span> : r.available ? <Link to={`/admin/appointments/${r.appointment_id}`} className="text-xs font-semibold text-[#b75b28]">Open ↗</Link> : <span className="text-xs text-[#8a8c80]">Visit removed</span>}
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
